import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchTasks, toggleDrawer, setFilter, reorderTasks } from "../../Redux/slices/TaskSlice";
import { IoClose } from "react-icons/io5";
import TaskItem from "./TaskList/TaskItem";
import TaskDetailModal from "./TaskDetailModal/TaskDetailModal";
import QuickAddTask from "./QuickAddTask/QuickAddTask"; // Added
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';

const TaskDrawer = () => {
    const dispatch = useDispatch();
    const { isDrawerOpen, tasks, activeFilter, isLoading } = useSelector((state) => state.task);
    const { conversationId } = useSelector((state) => state.chatUser);

    // Local state for tasks to handle drag and drop smoothly
    const [localTasks, setLocalTasks] = useState([]);
    const [selectedTask, setSelectedTask] = useState(null);

    useEffect(() => {
        if (isDrawerOpen) {
            // Fetch tasks for current conversation OR personal tasks ('me')
            const targetId = conversationId || 'me';
            dispatch(fetchTasks(targetId));
        }
    }, [isDrawerOpen, conversationId, dispatch]);

    useEffect(() => {
        setLocalTasks(tasks);
    }, [tasks]);

    const activeTasks = localTasks.filter(t => !t.isCompleted);
    const completedTasks = localTasks.filter(t => t.isCompleted);

    // Filter logic for tabs
    const getFilteredTasks = () => {
        switch (activeFilter) {
            case 'completed':
                return completedTasks;
            case 'me':
                const myId = localStorage.getItem("userId");
                return activeTasks.filter(t => t.assignedUserId?._id === myId);
            default: // all active
                return activeTasks;
        }
    };

    const filteredTasks = getFilteredTasks();

    // Separate tasks into "Due Soon" and "To Do" for the 'all' or 'me' view
    const dueTasks = filteredTasks.filter(t => t.dueDate).sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
    const todoTasks = filteredTasks.filter(t => !t.dueDate).sort((a, b) => a.orderIndex - b.orderIndex);

    // DnD Sensors
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active.id !== over.id) {
            setLocalTasks((items) => {
                const oldIndex = items.findIndex(i => i._id === active.id);
                const newIndex = items.findIndex(i => i._id === over.id);
                const newItems = arrayMove(items, oldIndex, newIndex);

                // Prepare bulk update payload
                const reorderedSection = newItems.filter(t => !t.dueDate && !t.isCompleted);
                const updates = reorderedSection.map((t, index) => ({ _id: t._id, orderIndex: index }));
                dispatch(reorderTasks(updates));

                return newItems;
            });
        }
    };

    if (!isDrawerOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex justify-end overflow-hidden">
            {/* Transparent Backdrop - allows seeing content behind but catches clicks to close */}
            <div
                className="absolute inset-0 bg-transparent"
                onClick={() => dispatch(toggleDrawer(false))}
            />

            {/* Drawer - Enforce right-0 explicitly */}
            <div className="fixed top-0 right-0 h-full w-[400px] bg-white/90 backdrop-blur-xl shadow-2xl border-l border-white/20 transform transition-transform duration-300 ease-in-out flex flex-col z-[60]">

                {/* Header */}
                <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                    <h2 className="text-xl font-bold text-gray-800">
                        {conversationId ? 'Tasks' : 'My Personal Tasks'}
                    </h2>
                    <button
                        onClick={() => dispatch(toggleDrawer(false))}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <IoClose size={24} className="text-gray-500" />
                    </button>
                </div>

                {/* Quick Add Task */}
                {activeFilter !== 'completed' && (
                    <div className="mt-2">
                        <QuickAddTask />
                    </div>
                )}

                {/* Filters */}
                <div className="flex p-2 gap-2 bg-gray-50/50 mx-4 mt-2 rounded-xl">
                    {['all', 'me', 'completed'].map((filter) => (
                        <button
                            key={filter}
                            onClick={() => dispatch(setFilter(filter))}
                            className={`flex-1 py-1.5 text-sm font-medium rounded-lg transition-all ${activeFilter === filter
                                ? 'bg-indigo-600 text-white shadow-sm'
                                : 'text-gray-600 hover:bg-white/60'
                                }`}
                        >
                            {filter === 'all' ? 'All Active' : filter === 'me' ? 'My Tasks' : 'Completed'}
                        </button>
                    ))}
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4 space-y-6">
                    {isLoading ? (
                        <div className="flex justify-center py-10">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                        </div>
                    ) : (
                        <>
                            {/* Due Soon Section (No DnD) */}
                            {activeFilter !== 'completed' && dueTasks.length > 0 && (
                                <div>
                                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 ml-1">Due Soon</h3>
                                    <div className="space-y-2">
                                        {dueTasks.map(task => (
                                            <TaskItem key={task._id} task={task} onClick={() => setSelectedTask(task)} />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* To Do Section (DnD Enabled) */}
                            {todoTasks.length > 0 && activeFilter !== 'completed' && (
                                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                    <SortableContext items={todoTasks.map(t => t._id)} strategy={verticalListSortingStrategy}>
                                        <div>
                                            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 ml-1">To Do</h3>
                                            <div className="space-y-2">
                                                {todoTasks.map(task => (
                                                    <TaskItem key={task._id} task={task} onClick={() => setSelectedTask(task)} isSortable />
                                                ))}
                                            </div>
                                        </div>
                                    </SortableContext>
                                </DndContext>
                            )}

                            {/* Completed List (No DnD) */}
                            {activeFilter === 'completed' && (
                                <div className="space-y-2">
                                    {filteredTasks.map(task => (
                                        <TaskItem key={task._id} task={task} onClick={() => setSelectedTask(task)} />
                                    ))}
                                    {filteredTasks.length === 0 && <p className="text-center text-gray-500 py-10">No completed tasks yet.</p>}
                                </div>
                            )}

                            {/* Empty State */}
                            {activeFilter !== 'completed' && dueTasks.length === 0 && todoTasks.length === 0 && (
                                <p className="text-center text-gray-500 py-10">No active tasks.</p>
                            )}
                        </>
                    )}
                </div>

                {/* Removed bottom "Create Button" */}
            </div>

            {/* Detail Modal */}
            {selectedTask && (
                <TaskDetailModal
                    task={selectedTask}
                    onClose={() => setSelectedTask(null)}
                    conversationId={conversationId}
                />
            )}
        </div>
    );
};

export default TaskDrawer;
